import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SplitButton } from "@/components/ui/split-button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import { Mail } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { SendChannel, SEND_CHANNELS } from "@/entities/dreamerEmail/types";
import { DraftEmailSend as SendIcon } from "@dreamhub/designsystem/icons";
import GmailIcon from "@/assets/icons/draftEmail/GmailIcon.png";
import OutlookIcon from "@/assets/icons/draftEmail/OutlookIcon.png";
import { useDefaultEmailProvider } from "./useDefaultEmailProvider";
import { useOpenDraftInEmailApp } from "./useOpenDraftInEmailApp";

interface Props {
  subject: string;
  body: string;
  recipientEmails?: string[];
  disabled?: boolean;
}


// One-off gradient from the design spec — no Tailwind token maps to this
// provider-chip tint, so it's kept as an inline style constant.
const PROVIDER_CHIP_BG =
  "linear-gradient(221deg, rgba(255, 255, 255, 0.02) 6.73%, rgba(12, 17, 29, 0.02) 95.39%), rgba(152, 162, 179, 0.18)";

const chip = (content: ReactNode) => (
  <span
    className="flex size-6 shrink-0 items-center justify-center rounded-lg border-[0.5px] border-white/24"
    style={{ background: PROVIDER_CHIP_BG }}
  >
    {content}
  </span>
);

const channelIcon = (channel: SendChannel) => {
  switch (channel) {
    case "gmail":
      return chip(
        <img src={GmailIcon} alt="" className="size-4 object-contain" />
      );
    case "outlook":
      return chip(
        <img src={OutlookIcon} alt="" className="size-4 object-contain" />
      );
    case "mailto":
      return chip(
        <IconContainer size="sm">
          <Mail />
        </IconContainer>
      );
  }
};

// Send control with two layouts:
//  - No default chosen: a single button ([send] Send [chevron]) that opens the
//    dropdown of all methods.
//  - Default chosen: a split button — the main CTA opens the default method's
//    compose, the chevron opens the dropdown of the remaining methods.
// Picking any method opens its compose and becomes the new sticky default.
const DraftEmailSendButton = ({
  subject,
  body,
  recipientEmails = [],
  disabled
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { defaultChannel, setDefaultChannel } = useDefaultEmailProvider();
  const openInApp = useOpenDraftInEmailApp();

  const handleOpenChange = (next: boolean) => {
    if (!disabled) setIsOpen(next);
  };

  const labelKey = (channel: SendChannel) =>
    channel === "mailto" ? "draftEmail.menu.openInMail" : `draftEmail.menu.${channel}`;

  const send = (channel: SendChannel) => {
    setDefaultChannel(channel);
    openInApp(channel, subject, body, recipientEmails);
    setIsOpen(false);
  };

  const menuChannels = SEND_CHANNELS.filter(
    (channel) => channel !== defaultChannel
  );

  const menu = (
    <DropdownMenuContent
      align="start"
      className="min-w-[200px] border-border-field-droplist bg-surface-widget-card dark:bg-surface-widget-card"
    >
      {menuChannels.map((channel) => (
        <DropdownMenuItem
          key={channel}
          onSelect={() => send(channel)}
          data-test={`draft-email-send-${channel}`}
          className="focus:bg-surface-hover-soft dark:focus:bg-surface-hover-soft"
        >
          <span className="flex items-center gap-2">
            {channelIcon(channel)}
            <Text as="span" variant="body">
              {t(labelKey(channel))}
            </Text>
          </span>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );

  if (!defaultChannel) {
    return (
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="lg"
            disabled={disabled}
            data-test="draft-email-send"
            aria-label={t("draftEmail.send")}
          >
            <SendIcon />
            {t("draftEmail.send")}
            <ChevronDown
              className={cn(
                "h-5 transition-transform duration-200",
                isOpen ? "rotate-180" : "rotate-0"
              )}
            />
          </Button>
        </DropdownMenuTrigger>
        {menu}
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <SplitButton
        data-test="draft-email-send"
        mainButtonClassName="whitespace-nowrap"
        mainButtonDisabled={disabled}
        splitButtonDisabled={disabled}
        splitButtonClassName={cn(disabled && "opacity-50")}
        mainContent={
          <span className="flex items-center gap-1">
            <SendIcon />
            {t(labelKey(defaultChannel))}
          </span>
        }
        onMainClick={() => send(defaultChannel)}
        isOpen={isOpen}
        splitButtonAsChild
        splitContent={<DropdownMenuTrigger asChild />}
      />
      {menu}
    </DropdownMenu>
  );
};

export default DraftEmailSendButton;
